import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';

interface PhoneCountryOption {
  iso2: CountryCode;
  name: string;
  dialCode: string;
}

@Component({
  selector: 'app-international-phone-input',
  templateUrl: './international-phone-input.component.html',
  styleUrls: ['./international-phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InternationalPhoneInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InternationalPhoneInputComponent),
      multi: true,
    },
  ],
})
export class InternationalPhoneInputComponent
  implements ControlValueAccessor, Validator
{
  @Input() inputId = 'phone';
  @Input() defaultCountry: CountryCode = 'CR';
  @Input() placeholder = '';
  @Output() valueNormalized = new EventEmitter<string>();

  countries: PhoneCountryOption[] = [];
  filteredCountries: PhoneCountryOption[] = [];
  selectedCountry?: PhoneCountryOption;
  nationalNumber = '';
  searchText = '';
  isOpen = false;
  isDisabled = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {
    this.countries = getCountries()
      .map((iso2) => ({
        iso2,
        name: this.getCountryName(iso2),
        dialCode: getCountryCallingCode(iso2),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    this.filteredCountries = this.countries;
    this.selectedCountry =
      this.countries.find((country) => country.iso2 === this.defaultCountry) ||
      this.countries[0];
  }

  writeValue(value: string | null): void {
    if (!value) {
      this.nationalNumber = '';
      return;
    }

    const parsed =
      parsePhoneNumberFromString(value) ||
      parsePhoneNumberFromString(value, this.selectedCountry?.iso2);

    if (parsed?.country) {
      this.selectedCountry =
        this.countries.find((country) => country.iso2 === parsed.country) ||
        this.selectedCountry;
      this.nationalNumber = this.formatNationalNumber(parsed.nationalNumber);
      return;
    }

    this.nationalNumber = value.replace(/^\+\d+\s*/, '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value && !this.nationalNumber) {
      return null;
    }

    if (!this.selectedCountry) {
      return { countryCodeRequired: true };
    }

    const phoneNumber = parsePhoneNumberFromString(
      this.nationalNumber,
      this.selectedCountry.iso2
    );

    return phoneNumber?.isValid() ? null : { phoneInvalid: true };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  toggleDropdown(): void {
    if (this.isDisabled) {
      return;
    }
    this.isOpen = !this.isOpen;
    this.searchText = '';
    this.filteredCountries = this.countries;
  }

  filterCountries(): void {
    const search = this.searchText.trim().toLowerCase().replace(/^\+/, '');
    this.filteredCountries = this.countries.filter((country) => {
      return (
        country.name.toLowerCase().includes(search) ||
        country.iso2.toLowerCase().includes(search) ||
        country.dialCode.includes(search)
      );
    });
  }

  selectCountry(country: PhoneCountryOption): void {
    this.selectedCountry = country;
    this.isOpen = false;
    this.emitPhoneValue();
  }

  onPhoneInput(value: string): void {
    this.nationalNumber = this.formatNationalNumber(value);
    this.emitPhoneValue();
  }

  markAsTouched(): void {
    this.onTouched();
  }

  get flagClass(): string {
    return this.selectedCountry
      ? `fi fi-${this.selectedCountry.iso2.toLowerCase()}`
      : 'fi';
  }

  @HostListener('document:click', ['$event.target'])
  closeOnOutsideClick(target: HTMLElement): void {
    if (!this.elementRef.nativeElement.contains(target)) {
      this.isOpen = false;
    }
  }

  private emitPhoneValue(): void {
    const normalized = this.getNormalizedPhone();
    this.onChange(normalized);
    this.valueNormalized.emit(normalized);
    this.onValidatorChange();
  }

  private getNormalizedPhone(): string {
    if (!this.nationalNumber || !this.selectedCountry) {
      return '';
    }

    const phoneNumber = parsePhoneNumberFromString(
      this.nationalNumber,
      this.selectedCountry.iso2
    );

    return phoneNumber?.number || '';
  }

  private formatNationalNumber(value: string): string {
    if (!this.selectedCountry) {
      return value;
    }
    return new AsYouType(this.selectedCountry.iso2).input(value);
  }

  private getCountryName(countryCode: CountryCode): string {
    const intlWithDisplayNames = Intl as typeof Intl & {
      DisplayNames?: new (
        locales: string[],
        options: { type: 'region' }
      ) => { of: (code: string) => string | undefined };
    };
    const displayNames = intlWithDisplayNames.DisplayNames
      ? new intlWithDisplayNames.DisplayNames(['es'], { type: 'region' })
      : null;
    return displayNames?.of(countryCode) || countryCode;
  }
}
